/* Doctor portal: overview, my prescriptions (create/approve), profile with signature picker. */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  LayoutDashboard, FileSignature, UserRound, Phone, X, Plus, Trash2, CheckCircle2, FileText, AlertTriangle, ArrowUpDown, CalendarDays, Search,
} from "lucide-react";
import config from "../../config";
import { fieldError } from "../../lib/validation";
import { formatDateDMY } from "../../lib/dates";
import {
  useStaffSession, StaffLogin, StaffShell, SectionCard, StatCard, StatusChip,
  staffHeaders, detailMessage, openAuthedFile, paymentSucceeded,
} from "./staffShared";

const SIGNATURE_STYLES = [
  { id: "classic", label: "Classic", family: "'Great Vibes', cursive" },
  { id: "flow", label: "Flowing", family: "'Dancing Script', cursive" },
  { id: "bold", label: "Brush", family: "'Alex Brush', cursive" },
];

function useSignatureFonts() {
  useEffect(() => {
    if (document.getElementById("rx-sig-fonts")) return;
    const link = document.createElement("link");
    link.id = "rx-sig-fonts";
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Great+Vibes&family=Dancing+Script:wght@600&family=Alex+Brush&display=swap";
    document.head.appendChild(link);
  }, []);
}

function fmtDate(d) {
  return formatDateDMY(d);
}

/* ── Create prescription modal ──────────────────────────────────────────── */

function RxModal({ booking, onClose, onDone, mode = "create" }) {
  const [options, setOptions] = useState(null);
  const [rows, setRows] = useState([{ vaccine_id: "", vaccine_name: "", dosage: "" }]);
  const [notes, setNotes] = useState(booking.doctor_notes || "");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const reviewOnly = mode === "review";

  useEffect(() => {
    axios.get(`${config.API}/doctor/rx-options`, { headers: staffHeaders() })
      .then(({ data }) => {
        setOptions(data);
        // Preselect the booked vaccine when it matches the catalogue
        const match = (data.vaccines || []).find((v) => v.name === (booking.service_name || booking.vaccine_name));
        if (match) {
          setRows([{ vaccine_id: match.id, vaccine_name: match.name, dosage: match.dosage_options?.[0] || "" }]);
        }
      })
      .catch((err) => setError(detailMessage(err)));
  }, [booking]);

  const setRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const pickVaccine = (idx, vaccineId) => {
    const v = (options?.vaccines || []).find((x) => x.id === vaccineId);
    setRow(idx, {
      vaccine_id: vaccineId,
      vaccine_name: v?.name || "",
      dosage: v?.dosage_options?.[0] || "",
    });
  };

  const dosagesFor = (row) => {
    const v = (options?.vaccines || []).find((x) => x.id === row.vaccine_id);
    return v?.dosage_options || options?.default_dosages || [];
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!notes.trim()) { setError("Add notes for the nurse"); return; }
    setLoading(true);
    setError("");
    try {
      if (reviewOnly) {
        await axios.post(`${config.API}/doctor/prescriptions/review`,
          { booking_id: booking.id, notes: notes.trim() }, { headers: staffHeaders() });
      } else {
        const items = rows.filter((r) => r.vaccine_name && r.dosage);
        if (!items.length) { setError("Pick at least one vaccine + dosage"); setLoading(false); return; }
        await axios.post(`${config.API}/doctor/prescriptions`,
          { booking_id: booking.id, items, notes: notes.trim() }, { headers: staffHeaders() });
      }
      onDone();
    } catch (err) {
      setError(detailMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl p-6 w-full max-w-xl space-y-4 my-8" data-testid="rx-modal">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-[#311b52]" style={{ fontFamily: "Poppins" }}>{reviewOnly ? "Review prescription" : "Create prescription"}</h3>
            <p className="text-sm text-[#673c96]">{booking.beneficiary_name || booking.customer_name} · {booking.service_name || booking.vaccine_name}</p>
          </div>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-[#673c96]" /></button>
        </div>

        {!options && !reviewOnly ? (
          <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#673c96] border-t-transparent" /></div>
        ) : (
          <>
            {!reviewOnly && (
            <div className="space-y-3">
              {rows.map((row, idx) => (
                <div key={idx} className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center rounded-xl border border-[#e5e7eb] p-3">
                  <select value={row.vaccine_id} onChange={(e) => pickVaccine(idx, e.target.value)} required
                    className="flex-1 min-w-0 rounded-xl border border-[#673c96]/20 px-3 py-2.5 text-sm bg-white" data-testid={`rx-vaccine-${idx}`}>
                    <option value="">Select vaccine…</option>
                    {(options.vaccines || []).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                  <select value={row.dosage} onChange={(e) => setRow(idx, { dosage: e.target.value })} required
                    className="flex-1 min-w-0 rounded-xl border border-[#673c96]/20 px-3 py-2.5 text-sm bg-white" data-testid={`rx-dosage-${idx}`}>
                    <option value="">Dosage…</option>
                    {dosagesFor(row).map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  {rows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev))}
                      className="inline-flex items-center justify-center gap-1.5 shrink-0 px-3 py-2.5 rounded-xl border border-red-200 text-red-600 text-sm font-semibold hover:bg-red-50"
                      aria-label="Remove vaccine row"
                      data-testid={`rx-remove-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" /> Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
            )}
            {!reviewOnly && (
              <>
                <button type="button" onClick={() => setRows([...rows, { vaccine_id: "", vaccine_name: "", dosage: "" }])}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#673c96]">
                  <Plus className="w-4 h-4" /> Add another vaccine
                </button>
                <div className="bg-[#faf8fc] rounded-xl p-3 text-xs text-[#6b7280]">
                  A BookMyJab prescription pad PDF is generated with the patient line, vaccine, dosage, your notes, stamp and signature, then attached to the booking for the nurse.
                </div>
              </>
            )}
            <div>
              <p className="text-sm font-semibold text-[#311b52] mb-1">Notes for the nurse *</p>
              <textarea required value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                placeholder="Dosage instructions, allergies, follow-up, anything the nurse should know"
                className="w-full rounded-xl border border-[#673c96]/20 px-3 py-2.5 text-sm"
                data-testid="rx-notes" />
            </div>
            {error && <p className="text-sm text-[#ee4036]">{error}</p>}
            <button disabled={loading} className="w-full bg-[#f37721] text-white rounded-full py-3 font-semibold" data-testid="rx-approve">
              {loading ? "Saving…" : (reviewOnly ? "Save notes & approve for nurse" : "Review & approve prescription")}
            </button>
          </>
        )}
      </form>
    </div>
  );
}

/* ── Prescriptions queue ────────────────────────────────────────────────── */

function PrescriptionsSection({ profileComplete, goProfile }) {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rxFor, setRxFor] = useState(null);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [sortKey, setSortKey] = useState("preferred_date");
  const [sortDir, setSortDir] = useState("asc");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${config.API}/doctor/prescriptions`, { params: { status: tab }, headers: staffHeaders() })
      .then(({ data }) => {
        const items = data.items || [];
        setItems(tab === "pending" ? items.filter((b) => paymentSucceeded(b.payment_status)) : items);
      })
      .catch((err) => setError(detailMessage(err)))
      .finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const filteredItems = items.filter((row) => {
    const needle = search.trim().toLowerCase();
    if (!needle) return true;
    const blob = [row.id, row.beneficiary_name, row.customer_name, row.customer_phone, row.service_name, row.vaccine_name]
      .map((v) => String(v || "").toLowerCase()).join(" ");
    return blob.includes(needle);
  });
  const sortedItems = [...filteredItems].sort((a, b) => {
    const pick = (row) => {
      if (sortKey === "patient") return String(row.beneficiary_name || row.customer_name || "").toLowerCase();
      if (sortKey === "vaccine") return String(row.service_name || row.vaccine_name || "").toLowerCase();
      if (sortKey === "created_at") return String(row.rx_created_at || row.created_at || "");
      if (sortKey === "id") return String(row.id || "");
      return `${row.preferred_date || ""} ${row.preferred_slot || ""}`;
    };
    const av = pick(a);
    const bv = pick(b);
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <div className="space-y-4">
      {!profileComplete && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex flex-wrap items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <p className="flex-1 text-sm text-amber-800 min-w-[200px]">
            Complete your profile (license, address, fellowship, hospital, email, signature) before creating prescriptions.
          </p>
          <button type="button" onClick={goProfile} className="text-sm font-semibold text-[#673c96] underline">Complete profile</button>
        </div>
      )}

      <SectionCard title="My prescriptions" subtitle="Review every booking. If the customer uploaded a file, add notes. If they booked without a file, create a prescription — a doctor call is expected.">
        <div className="flex gap-2">
          {[["pending", "Pending"], ["completed", "Created by me"]].map(([id, label]) => (
            <button key={id} type="button" onClick={() => setTab(id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold ${tab === id ? "bg-[#311b52] text-white" : "bg-[#f4f5f7] text-[#673c96]"}`}
              data-testid={`rx-tab-${id}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="rx-sort-bar">
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#673c96]"><ArrowUpDown className="w-3.5 h-3.5" /> Sort</span>
          <select value={sortKey} onChange={(e) => { setSortKey(e.target.value); setPage(1); }}
            className="rounded-full border border-[#673c96]/20 px-3 py-1.5 text-xs bg-white">
            <option value="preferred_date">Visit date</option>
            <option value="created_at">Created</option>
            <option value="patient">Patient name</option>
            <option value="vaccine">Vaccine</option>
            <option value="id">Booking ID</option>
          </select>
          <select value={sortDir} onChange={(e) => { setSortDir(e.target.value); setPage(1); }}
            className="rounded-full border border-[#673c96]/20 px-3 py-1.5 text-xs bg-white">
            <option value="asc">Ascending / chronological</option>
            <option value="desc">Descending / reverse chronological</option>
          </select>
          <div className="relative flex-1 min-w-[180px]">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#673c96]" />
            <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Find patient, vaccine, phone, booking ID"
              className="w-full rounded-full border border-[#673c96]/20 pl-8 pr-3 py-1.5 text-xs" data-testid="rx-search" />
          </div>
        </div>
      </SectionCard>

      {msg && <p className="text-sm text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> {msg}</p>}
      {error && <p className="text-sm text-[#ee4036]">{error}</p>}

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#673c96] border-t-transparent" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-[#673c96]/25 p-10 text-center text-[#673c96]">
          {tab === "pending" ? "No bookings waiting on a prescription." : "You haven't created any prescriptions yet."}
        </div>
      ) : sortedItems.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-[#673c96]/25 p-10 text-center text-[#673c96]">No bookings match that search.</div>
      ) : (
        <div className="grid gap-3">
          {sortedItems.slice((page - 1) * pageSize, page * pageSize).map((b) => (
            <div key={b.id} className="bg-white rounded-2xl border border-[#673c96]/10 p-4" data-testid={`rx-booking-${b.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <p className="font-semibold text-[#311b52]" style={{ fontFamily: "Poppins" }}>{b.beneficiary_name || b.customer_name}</p>
                  <p className="text-sm text-[#673c96]">{b.service_name || b.vaccine_name} · {fmtDate(b.preferred_date)} {b.preferred_slot || ""}</p>
                </div>
                <StatusChip status={b.status} />
              </div>
              {b.prescription_note && <p className="text-xs text-[#6b7280] mt-1.5">Customer note: {b.prescription_note}</p>}
              {b.doctor_notes && <p className="text-xs text-[#311b52] mt-1.5">Doctor notes: {b.doctor_notes}</p>}
              <p className="text-xs text-[#673c96] mt-1">
                {(b.prescription_status || "").replaceAll("_", " ") === "not required" || b.prescription_status === "not_required"
                  ? "No file uploaded — doctor consultation"
                  : (b.patient_prescription_file_reference ? "Customer uploaded a file" : "Needs doctor prescription")}
              </p>
              {tab === "completed" && (b.rx_items || []).length > 0 && (
                <p className="text-xs text-[#6b7280] mt-1.5">
                  {(b.rx_items || []).map((i) => `${i.vaccine_name} (${i.dosage})`).join(", ")}
                </p>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                {b.customer_phone && (
                  <a href={`tel:+${b.customer_phone}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#673c96]/30 text-[#673c96] text-sm font-semibold">
                    <Phone className="w-4 h-4" /> Call customer
                  </a>
                )}
                {tab === "pending" ? (
                  <>
                    {(b.patient_prescription_file_reference || b.prescription_file_reference) && (
                      <button type="button"
                        onClick={() => openAuthedFile(`${config.API}/doctor/bookings/${b.id}/prescription?kind=patient`, setError)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#673c96]/30 text-[#673c96] text-sm font-semibold">
                        <FileText className="w-4 h-4" /> View patient upload
                      </button>
                    )}
                    {(b.patient_prescription_file_reference || b.prescription_file_reference) && (
                      <button type="button" disabled={!profileComplete}
                        onClick={() => setRxFor({ booking: b, mode: "review" })}
                        className={`px-4 py-2 rounded-full text-sm font-semibold ${profileComplete ? "border border-[#673c96] text-[#673c96]" : "bg-gray-200 text-gray-500 cursor-not-allowed"}`}>
                        Add notes & approve
                      </button>
                    )}
                    <button type="button" disabled={!profileComplete}
                      onClick={() => setRxFor({ booking: b, mode: "create" })}
                      className={`px-4 py-2 rounded-full text-sm font-semibold ${profileComplete ? "bg-[#f37721] text-white" : "bg-gray-200 text-gray-500 cursor-not-allowed"}`}
                      data-testid={`create-rx-${b.id}`}>
                      Create prescription
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button"
                      onClick={() => openAuthedFile(`${config.API}/doctor/bookings/${b.id}/prescription`, setError)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#673c96] text-white text-sm font-semibold">
                      <FileText className="w-4 h-4" /> View prescription
                    </button>
                    {(b.patient_prescription_file_reference && b.patient_prescription_file_reference !== b.prescription_file_reference) && (
                      <button type="button"
                        onClick={() => openAuthedFile(`${config.API}/doctor/bookings/${b.id}/prescription?kind=patient`, setError)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[#673c96]/30 text-[#673c96] text-sm font-semibold">
                        Patient upload
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
          {sortedItems.length > pageSize && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="text-sm font-semibold text-[#673c96] disabled:opacity-40">Previous</button>
              <span className="text-xs text-[#6b7280]">Page {page} of {Math.max(1, Math.ceil(sortedItems.length / pageSize))}</span>
              <button type="button" disabled={page * pageSize >= sortedItems.length} onClick={() => setPage((p) => p + 1)} className="text-sm font-semibold text-[#673c96] disabled:opacity-40">Next</button>
            </div>
          )}
        </div>
      )}

      {rxFor && (
        <RxModal booking={rxFor.booking || rxFor} mode={rxFor.mode || "create"} onClose={() => setRxFor(null)}
          onDone={() => { setRxFor(null); setMsg("Saved. The nurse can now see this prescription and your notes."); setTab("completed"); load(); }} />
      )}
    </div>
  );
}

/* ── Profile ────────────────────────────────────────────────────────────── */

function DoctorProfileSection({ onSaved }) {
  useSignatureFonts();
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${config.API}/doctor/profile`, { headers: staffHeaders() })
      .then(({ data }) => setForm(data))
      .catch((err) => setError(detailMessage(err)));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg("");
    setError("");
    try {
      const err = fieldError({ name: form.full_name, email: form.email });
      if (err) { setError(err); return; }
      const { data } = await axios.patch(`${config.API}/doctor/profile`, {
        full_name: form.full_name,
        email: form.email,
        license_number: form.license_number,
        address: form.address,
        fellowship: form.fellowship,
        hospital: form.hospital,
        signature_style: form.signature_style,
        signature_name: form.signature_name || form.full_name,
      }, { headers: staffHeaders() });
      setForm(data);
      setMsg(data.profile_complete ? "Profile saved — you can now create prescriptions." : "Saved. Fill the remaining mandatory fields to unlock prescriptions.");
      onSaved(data);
    } catch (err) {
      setError(detailMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#673c96] border-t-transparent" /></div>;
  }

  const sigName = form.signature_name || form.full_name || "Your Name";
  const fields = [
    ["full_name", "Full name *"],
    ["license_number", "Medical license / registration no. *"],
    ["email", "Email *"],
    ["hospital", "Hospital / clinic *"],
    ["fellowship", "Fellowship & qualifications *"],
    ["address", "Practice address *"],
  ];

  return (
    <SectionCard title="Doctor profile" subtitle="All fields are mandatory — they appear on every prescription you approve.">
      <form onSubmit={save} className="space-y-4 max-w-lg">
        {fields.map(([key, label]) => (
          <div key={key}>
            <label className="text-xs font-semibold text-[#311b52] block mb-1">{label}</label>
            {key === "address" ? (
              <textarea required value={form[key] || ""} rows={2}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                className="w-full rounded-xl border border-[#673c96]/20 px-4 py-3" data-testid={`doctor-${key}`} />
            ) : (
              <input required type={key === "email" ? "email" : "text"} value={form[key] || ""}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                className="w-full rounded-xl border border-[#673c96]/20 px-4 py-3" data-testid={`doctor-${key}`} />
            )}
          </div>
        ))}

        <div>
          <label className="text-xs font-semibold text-[#311b52] block mb-2">Upload PNG signature (used on prescriptions)</label>
          <input type="file" accept="image/png" className="block w-full text-sm mb-2" onChange={async (e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (!file) return;
            try {
              const fd = new FormData();
              fd.append("signature", file);
              const { data } = await axios.post(`${config.API}/doctor/profile/signature`, fd, { headers: staffHeaders() });
              setForm(data);
              setMsg("PNG signature uploaded — it will appear on prescriptions.");
              onSaved(data);
            } catch (err) {
              setError(detailMessage(err) || "Could not upload signature");
            }
          }} data-testid="doctor-signature-upload" />
          {form.has_signature && <p className="text-xs text-emerald-700 mb-3">A PNG signature is on file and will print on the prescription pad.</p>}
          <label className="text-xs font-semibold text-[#311b52] block mb-2">Or pick a typed signature style</label>
          <div className="grid sm:grid-cols-3 gap-3">
            {SIGNATURE_STYLES.map((s) => (
              <button key={s.id} type="button" onClick={() => setForm({ ...form, signature_style: s.id })}
                className={`rounded-2xl border-2 p-4 text-center transition-colors ${form.signature_style === s.id ? "border-[#673c96] bg-[#673c96]/5" : "border-[#673c96]/15 bg-white hover:border-[#673c96]/40"}`}
                data-testid={`signature-${s.id}`}>
                <span className="block text-2xl text-[#311b52] leading-snug" style={{ fontFamily: s.family }}>{sigName}</span>
                <span className="block mt-2 text-[11px] font-semibold uppercase tracking-wider text-[#673c96]/70">{s.label}</span>
              </button>
            ))}
          </div>
        </div>

        {msg && <p className="text-sm text-emerald-700">{msg}</p>}
        {error && <p className="text-sm text-[#ee4036]">{error}</p>}
        <button disabled={saving} className="px-6 py-3 rounded-full bg-[#f37721] text-white font-semibold" data-testid="doctor-profile-save">
          {saving ? "Saving…" : "Save profile"}
        </button>
      </form>
    </SectionCard>
  );
}

/* ── Bookings list ──────────────────────────────────────────────────────── */

function BookingsSection() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("created_at");
  const [dir, setDir] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setQ(qInput), 350);
    return () => clearTimeout(t);
  }, [qInput]);

  useEffect(() => { setPage(1); }, [q]);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${config.API}/doctor/bookings`, {
      params: { q, sort, dir, page, limit: 10 },
      headers: staffHeaders(),
    })
      .then(({ data }) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
        setPages(data.pages || 1);
      })
      .catch((err) => setError(detailMessage(err)))
      .finally(() => setLoading(false));
  }, [q, sort, dir, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <SectionCard title="Bookings" subtitle="Find and sort live appointments. Search by patient, phone, vaccine or booking ID.">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#673c96]" />
            <input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Find bookings"
              className="w-full rounded-full border border-[#673c96]/20 pl-9 pr-3 py-2 text-sm" data-testid="doctor-booking-search" />
          </div>
          <select value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }} className="rounded-full border border-[#673c96]/20 px-3 py-2 text-sm bg-white">
            <option value="created_at">Created</option>
            <option value="preferred_date">Visit date</option>
            <option value="patient">Patient</option>
            <option value="vaccine">Vaccine</option>
            <option value="status">Status</option>
            <option value="id">Booking ID</option>
          </select>
          <select value={dir} onChange={(e) => setDir(e.target.value)} className="rounded-full border border-[#673c96]/20 px-3 py-2 text-sm bg-white">
            <option value="desc">Newest first</option>
            <option value="asc">Oldest first</option>
          </select>
        </div>
      </SectionCard>
      {error && <p className="text-sm text-[#ee4036]">{error}</p>}
      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#673c96] border-t-transparent" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-[#673c96]/25 p-10 text-center text-[#673c96]">No bookings found.</div>
      ) : (
        <div className="grid gap-3">
          {items.map((b) => (
            <div key={b.id} className="bg-white rounded-2xl border border-[#673c96]/10 p-4" data-testid={`doctor-booking-${b.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <p className="font-semibold text-[#311b52]" style={{ fontFamily: "Poppins" }}>{b.beneficiary_name || b.customer_name}</p>
                  <p className="text-sm text-[#673c96]">{b.service_name || b.vaccine_name} · {fmtDate(b.preferred_date)} {b.preferred_slot || ""}</p>
                  <p className="text-xs text-[#6b7280] mt-1">{b.id}{b.customer_phone ? ` · ${b.customer_phone}` : ""}</p>
                </div>
                <StatusChip status={b.status} />
              </div>
            </div>
          ))}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-1">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="text-sm font-semibold text-[#673c96] disabled:opacity-40">Previous</button>
              <span className="text-xs text-[#6b7280]">Page {page} of {pages} · {total} bookings</span>
              <button type="button" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="text-sm font-semibold text-[#673c96] disabled:opacity-40">Next</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Overview ───────────────────────────────────────────────────────────── */

function DoctorOverview({ profile, goTo }) {
  const [pending, setPending] = useState([]);
  const [done, setDone] = useState([]);

  useEffect(() => {
    axios.get(`${config.API}/doctor/prescriptions`, { params: { status: "pending" }, headers: staffHeaders() })
      .then(({ data }) => setPending((data.items || []).filter((b) => paymentSucceeded(b.payment_status)))).catch(() => {});
    axios.get(`${config.API}/doctor/prescriptions`, { params: { status: "completed" }, headers: staffHeaders() })
      .then(({ data }) => setDone(data.items || [])).catch(() => {});
  }, []);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatCard label="Awaiting prescription" value={pending.length} accent />
        <StatCard label="Created by me" value={done.length} />
        <StatCard label="Profile" value={profile?.profile_complete ? "Complete" : "Incomplete"} />
      </div>
      <SectionCard title="Next up" subtitle="Customers waiting on a doctor call & prescription"
        actions={<button type="button" onClick={() => goTo("prescriptions")} className="text-sm font-semibold text-[#673c96] underline">Open queue</button>}>
        {pending.length === 0 ? (
          <p className="text-sm text-[#6b7280]">Nothing pending — great job!</p>
        ) : (
          <div className="divide-y divide-[#673c96]/10">
            {pending.slice(0, 5).map((b) => (
              <div key={b.id} className="py-2.5 flex items-center justify-between gap-2 text-sm">
                <div>
                  <p className="font-medium text-[#311b52]">{b.beneficiary_name || b.customer_name}</p>
                  <p className="text-xs text-[#673c96]">{b.service_name || b.vaccine_name} · {fmtDate(b.preferred_date)}</p>
                </div>
                {b.customer_phone && (
                  <a href={`tel:+${b.customer_phone}`} className="text-[#f37721] font-semibold inline-flex items-center gap-1">
                    <Phone className="w-4 h-4" /> Call
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

/* ── Portal root ────────────────────────────────────────────────────────── */

export default function DoctorPortal() {
  const session = useStaffSession("doctor");
  const [tab, setTab] = useState("overview");
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (session.status !== "ready") return;
    axios.get(`${config.API}/doctor/profile`, { headers: staffHeaders() })
      .then(({ data }) => setProfile(data))
      .catch(() => {});
  }, [session.status]);

  if (session.status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f4f5f7]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#673c96] border-t-transparent" />
      </div>
    );
  }

  if (session.status === "login") {
    return (
      <StaffLogin role="doctor" title="Doctor portal" onAuthed={session.onAuthed}
        subtitle="Sign in with your registered phone to review bookings and issue prescriptions." />
    );
  }

  const nav = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "bookings", label: "Bookings", icon: CalendarDays },
    { id: "prescriptions", label: "My Prescriptions", icon: FileSignature },
    { id: "profile", label: "Profile", icon: UserRound, badge: profile && !profile.profile_complete ? "!" : null },
  ];

  return (
    <StaffShell roleLabel="Doctor" user={session.user} nav={nav} active={tab} onNavigate={setTab} onLogout={session.logout} portalPath="/doctor" notificationsPath="/doctor/notifications">
      {tab === "overview" && <DoctorOverview profile={profile} goTo={setTab} />}
      {tab === "bookings" && <BookingsSection />}
      {tab === "prescriptions" && (
        <PrescriptionsSection profileComplete={!!profile?.profile_complete} goProfile={() => setTab("profile")} />
      )}
      {tab === "profile" && <DoctorProfileSection onSaved={setProfile} />}
    </StaffShell>
  );
}
